<template>
  <div class="pdp pdp__container container">
    <AppBreadcrumbs />
    <!-- <AppHistoryBack class="pdp__back--link" /> -->

    <div class="pdp__layout">
      <div class="pdp__product">
        <div class="pdp__header">
          <h1 class="pdp__heading heading heading--5">
            {{ esProduct?.name }}
          </h1>

          <ProductReviewsScore
            class="pdp__score"
            :class="{ 'pdp__score--no-reviews': !m2Product.reviewStats.count }"
            :stats="m2Product.reviewStats"
          />
        </div>

        <div
          ref="addToCart"
          class="pdp__info"
        >
          <ProductAddToCart
            class="pdp__addtocart"
            :class="{ 'pdp__addtocart--no-sticky': makeAddToCartNonSticky }"
            :product="m2Product"
          >
            <template #below-promise>
              <ProductRelatedColorProducts
                class="pdp__colors"
                :product="esProduct"
              />
            </template>
          </ProductAddToCart>

          <LazyProductPromo
            v-for="promo in promos"
            :key="promo.documentId"
            class="pdp__promo"
            :promo="promo"
          />

          <div
            ref="addToCartTrigger"
            class="pdp__addtocart-trigger"
          />
          <LazyAppUsps
            v-if="extendedUsps?.length"
            class="pdp__usps"
            :usps="extendedUsps"
          />
        </div>
      </div>

      <div class="pdp__media">
        <ProductLabelBrand :product="esProduct" />

        <div class="pdp__labels">
          <ProductLabelMarketing :product="esProduct" />
        </div>

        <UiMediaGallery
          v-if="productMediaGallery"
          :name="`pdp-${esProduct.sku}`"
          :options="{
            behavior: 'smooth',
            dots: false,
            fullscreen: true,
            fullscreenOptions: {
              dots: false,
              navigation: true,
              thumbs: true,
              thumbsDirection: 'vertical',
              thumbsTrigger: 'click',
              XofY: true,
            },
            media: productMediaGallery,
            navigation: true,
            thumbs: true,
            thumbsDirection: 'vertical',
            thumbsTrigger: 'mouseover',
            XofY: false,
            imageSize: esProduct?.imageSize || 'M standaard',
          }"
        />
        <div
          id="videoly-videobox-placeholder"
          class="pdp__videoly"
        />
      </div>

      <ProductQonfi
        class="pdp__aiden"
        :sku="esProduct.sku"
        :title="$t('Which suitcase suits your trip?')"
        :subtitle="$t('Quickly discover which suitcase is suitable for your destination and way of travelling.')"
        :button-text="$t('Start the selection guide')"
        :button-link="m2Product.aidenAdvisorId"
        :strapi-data="aiden_aid_selectors[0] || null"
      />

      <div class="pdp__extra-data">
        <ProductUpsellProduct
          v-if="esProduct.upsellProduct"
          class="pdp__upsell-product"
          :product="esProduct"
        />

        <LazyUiCollapsable
          v-if="m2Product?.attributeGroups?.length"
          class="pdp__specifications specifications"
          :label="$t('Specifications')"
          open
        >
          <LazyUiCollapsable
            v-for="(attributeGroup, index) in m2Product?.attributeGroups"
            :key="attributeGroup.code"
            :label="$t(attributeGroup.name)"
            :open="!index"
            class="specifications__group"
          >
            <table class="specifications__table table table--spacing table--grid-rows">
              <tbody>
                <tr
                  v-for="spec in filteredAttributes(attributeGroup.attributes)"
                  :key="spec.code"
                >
                  <th>{{ spec.label }}</th>
                  <td>{{ spec.value }}</td>
                </tr>
              </tbody>
            </table>
          </LazyUiCollapsable>
        </LazyUiCollapsable>

        <LazyUiCollapsable
          v-if="m2Product?.description?.html?.trim()"
          class="pdp__description"
          :label="$t('Product description')"
        >
          <!-- <UiRichtext :html="m2Product.description.html" /> -->
          <div v-html="m2Product.description.html" />
        </LazyUiCollapsable>

        <LazyCatalogQuickLinks
          v-if="categoryBrandLinks.length"
          :title="$t('Related products')"
          :links="categoryBrandLinks"
          class="pdp__quick-links"
        />

        <div
          id="product-reviews"
          class="pdp__reviews-cta reviews-cta"
        >
          <template v-if="m2Product.reviewStats.count">
            <div class="reviews-cta__header">
              <div class="reviews-cta__reviews-score">
                <IconStar class="icon icon--star" />
                {{ m2Product.reviewStats.rating }} / 5.0
              </div>

              <div class="reviews-cta__reviews-count">
                {{ $t('Based on reviews', { count: m2Product.reviewStats.count }, m2Product.reviewStats.count) }}
              </div>
            </div>

            <button
              class="reviews-cta__button button button--cta button--center"
              type="button"
              @click="modalReviews.open"
            >
              <span>{{ $t('Show all reviews') }}</span>
              <IconCssArrow2 class="icon icon--arrow icon--arrow-right" />
            </button>
          </template>

          <template v-else>
            <div class="reviews-cta__header">
              <div class="reviews-cta__reviews-score">
                {{ $t('No reviews') }}
              </div>
            </div>

            <button
              class="reviews-cta__button button button--cta button--center"
              type="button"
              @click="modalReviewCreate.open"
            >
              <span>{{ $t('Add review') }}</span>
              <IconCssArrow2 class="icon icon--arrow icon--arrow-right" />
            </button>
          </template>

          <LazyUiDialog
            v-if="m2Product.reviewStats.count"
            name="modal-reviews"
            class="modal-reviews"
            dialog-type="responsive"
          >
            <template #dialogHeader>
              {{ $t('Reviews') }}
            </template>

            <ProductReviews :product="m2Product" />
          </LazyUiDialog>

          <LazyUiDialog
            class="modal-review-create"
            name="modal-review-create"
            dialog-type="responsive"
          >
            <template #dialogHeader>
              <span
                v-if="reviewCreateSuccess"
                v-text="$t('Thank you for adding review for', { product: esProduct.name })"
              />
              <span
                v-else
                v-text="$t('Create your review for', { product: esProduct.name })"
              />
            </template>
            <p v-if="reviewCreateSuccess">
              {{ $t('It takes up to 24 hours') }}
            </p>

            <ClientOnly v-else>
              <LazyFormReviewCreate

                :product="esProduct"
                @success="(val) => reviewCreateSuccess = val"
              />
            </ClientOnly>
          </LazyUiDialog>
        </div>

        <ProductTwSuggestions
          :product="esProduct"
          group-key="up-sell"
          :single="true"
        />

        <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-1" />

        <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-2" />

        <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-3" />

        <ProductCompareCta />

        <!-- <ProductSlider
          v-if="upsellProducts?.length"
          :products="upsellProducts"
          class="pdp__upsell upsell"
          :settings="{ wrapAround: false, snapAlign: 'start' }"
          :breakpoints="{}"
        >
          <div
            class="recently-viewed__heading heading heading--6"
            v-text="$t('Complete your look')"
          />
        </ProductSlider> -->

        <!-- <ProductSlider v-if="crosssellProducts?.length" :products="crosssellProducts" class="pdp__slider pdp__crosssell" :settings="{ wrapAround: false }">
          <h6 v-text="$t('Crosssell Products')" />
        </ProductSlider> -->

        <!-- <ProductSlider v-if="relatedProducts?.length" :products="relatedProducts" class="pdp__slider pdp__related" :settings="{ wrapAround: false }">
          <h6 v-text="$t('Related Products')" />
        </ProductSlider> -->

        <!-- <ProductSlider v-if="interestingProducts?.length" :products="interestingProducts" class="pdp__slider pdp__interesting" :settings="{ wrapAround: false }">
          <h6 v-text="$t('Interesting Products')" />
        </ProductSlider> -->

        <!-- <ProductSlider v-if="recentlyViewed?.length" :products="recentlyViewed" class="pdp__slider pdp__recently-viewed" :settings="{ wrapAround: false }">
          <h6 v-text="$t('Latest favourites')" />
        </ProductSlider> -->

        <!--  -->
        <!--  -->
        <!--  -->
        <!--  -->
        <!--  -->
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import GetPromosQuery from '~layers/app/graphql/pdp/getPromos.gql'
import getAidenAidSelectorForCategory from '~layers/app/graphql/pdp/getAidenAidSelector.gql'

const route = useRoute()
const { t: translate } = useI18n({ useScope: 'global' })
const meta = inject('meta', ref())
const { t } = useI18n({ useScope: 'global' })

const { public: config, app } = useRuntimeConfig()
const [_scheme, domain] = config.baseDomain.split('://')

const { sessionKey, profileKey, track: trackTweakwise } = useTweakwise()

const {
  m2Product,
  esProduct,
  attributes,
  parentCategories,
  categories,
  // crosssellProducts,
  // upsellProducts,
  // relatedProducts,
  // interestingProducts
  pdpUsps,
  fetchProductData,
} = usePDP(meta)

await fetchProductData()

const { data: promoData } = await useAsyncQuery<never>(GetPromosQuery, {
  sku: esProduct.value.sku,
  skuStart: esProduct.value.sku + ',',
  skuBetween: ',' + esProduct.value.sku + ',',
  skuEnd: ',' + esProduct.value.sku,
  brand: esProduct.value.brand.label,
  brandStart: esProduct.value.brand.label + ',',
  brandBetween: ',' + esProduct.value.brand.label + ',',
  brandEnd: ',' + esProduct.value.brand.label,
  parentKey: String(esProduct.value.attr_kat_parent_ext_key),
  parentKeyStart: String(esProduct.value.attr_kat_parent_ext_key) + ',',
  parentKeyBetween: ',' + String(esProduct.value.attr_kat_parent_ext_key) + ',',
  parentKeyEnd: ',' + String(esProduct.value.attr_kat_parent_ext_key),
  locale: config.strapiLocale,
  status: 'PUBLISHED',
})

const promos = computed<never[]>(() => {
  return (promoData?.value?.promos?.nodes || []).filter((item: any) => {
    const activeFrom = new Date(item.ActiveFrom).getTime()
    const activeUntil = new Date(item.ActiveUntil).getTime()
    const now = new Date()
    return activeFrom < now && activeUntil > now
  })
})

const rootCategory = computed<any | null>(() =>
  categories.value.find((category: any) => category.level === 2),
)

const { data: aidenAidSelectorData } = await useAsyncQuery(getAidenAidSelectorForCategory, {
  magentoCategoryId: rootCategory.value?.id || 0,
  locale: config.strapiLocale,
  status: 'PUBLISHED',
})

const aiden_aid_selectors = computed(() => {
  return aidenAidSelectorData.value?.aiden_aid_selectors?.nodes || []
})

provide('esProduct', esProduct)

const modalReviews = useUiDialog('modal-reviews')
const modalReviewCreate = useUiDialog('modal-review-create')

const reviewCreateSuccess = ref(false)
const addToCartTrigger = ref(null)
const makeAddToCartNonSticky = ref(false)

// const addToCart = ref<HTMLElement | null>(null)
// const addToCartIsVisible = useElementVisibility(addToCart)

const productMediaGallery = computed(() => {
  return (m2Product?.value?.media_gallery || []).toSorted((a, b) => {
    return a.position - b.position
  }).map((media: any) => {
    return {
      alt: media.label,
      src: media.url,
    }
  })
})

const extendedUsps = computed(() => {
  return [
    ...pdpUsps.value,
    {
      Label: translate('Warranty period', attributes.value),
      Description: null,
      Link: null,
      Icon: 'check',
    },
  ]
})

const categoryBrandLinks = computed(() => {
  const result: any[] = [
    {
      Name: esProduct.value.brand.label,
      Link: stringToSlug(esProduct.value.brand.label.replace('&', 'and')),
    },
  ]
  categories.value.forEach((category: any) => {
    if (category?.name && category?.url) {
      result.push({
        Name: category.name,
        Link: category.url,
      })
      if (esProduct?.value?.brand?.label && category?.level < 3) {
        result.push({
          Name: esProduct.value.brand.label + ' ' + category.name,
          Link: category.url + '/' + stringToSlug(esProduct.value.brand.label.replace('&', 'and')),
        })
      }
    }
  })
  return result
})

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: esProduct?.value?.name,
  parents: [...parentCategories.value].sort((a, b) => a.level - b.level),
})

const filteredAttributes = (attributes: any[]) => {
  return attributes.filter((spec: any) => {
    return spec.value && spec.value.trim() !== ''
  })
}

const { pageView, viewItem } = useDataLayer()
pageView('detail', esProduct?.value?.name)
viewItem(
  esProduct?.value,
  { skus: m2Product.value?.variants?.map((variant: any) => variant.product.sku) || [esProduct.value?.sku] },
)

const { getCompareProductListData, setRootCategory } = useCompareProductList()

onMounted(() => {
  if ('review' in route.query) {
    nextTick(() => {
      modalReviewCreate.open()
    })
  }

  trackTweakwise('pageview', {
    ProductKey: esProduct.value.tweakwiseId,
    ProfileKey: profileKey.value,
    SessionKey: sessionKey.value,
  })

  const {
    url: parentCategoryUrl,
    label: parentCategoryLabel,
  } = [...parentCategories.value].sort((a, b) => a.level - b.level)[0] || {}

  setRootCategory({
    url: parentCategoryUrl,
    name: parentCategoryLabel,
  })

  getCompareProductListData()

  // Setup intersection observer for add to cart trigger
  nextTick(() => {
    if (addToCartTrigger.value) {
      const observer = new IntersectionObserver(
        (entries) => {
          makeAddToCartNonSticky.value = entries[0]?.isIntersecting ?? false
        },
        {
          threshold: 0,
          rootMargin: '0px 0px -72px 0px',
        },
      )
      observer.observe(addToCartTrigger.value)

      onUnmounted(() => observer.disconnect())
    }
    else {
      // console.log('addToCartTrigger not found')
    }
  })
})

const metaTitle = computed(
  () => esProduct?.value?.name + ' ' + esProduct?.value?.sku,
)

const metaDescription = computed(
  () => t(`pdp.metaDescription`, {
    name: esProduct?.value?.name,
    sku: esProduct?.value?.sku,
    domain: domain.replace('www.', ''),
  }),
)

useHead({
  title: metaTitle,
  meta: [
    { hid: 'description', name: 'description', content: metaDescription },
    { hid: 'robots', name: 'robots', content: 'index, follow' },
  ],
})

const { canonical } = useSeo()
canonical(computed(() => route.path))

const productSnippet = () => {
  const { locale, locales } = useI18n({ useScope: 'global' })
  const currency = locales.value.find(lcl => lcl.code === locale.value)?.numberFormats.currency.currency || 'EUR'
  const reviewStats = m2Product.value?.reviewStats?.count ? m2Product.value?.reviewStats : null
  // const review = m2Product.value?.reviews?.items[0]

  // const reviews = review
  //   ? {
  //       '@type': 'Review',
  //       'reviewRating': {
  //         '@type': 'Rating',
  //         'ratingValue': review?.average_rating,
  //         'bestRating': 100,
  //       },
  //       'author': {
  //         '@type': 'Person',
  //         'name': review?.nickname,
  //       },
  //     }
  //   : undefined

  const aggregateRating = reviewStats
    ? {
        '@type': 'AggregateRating',
        'ratingValue': reviewStats?.rating,
        'reviewCount': reviewStats?.count,
      }
    : undefined

  useHead({
    script: [
      {
        'hid': 'Product',
        'type': 'application/ld+json',
        'data-nosnippet': true,
        'innerHTML': {
          '@context': 'https://schema.org',
          '@type': 'Product',
          'name': `${esProduct.value?.name} ${esProduct.value?.descriptiveName}`,
          'image': `${config.xcdnUrl}OQ75_RM830,1066,255,255,255/-/catalog/product${esProduct.value?.image}`,
          // 'description': this.metaDescription,
          'sku': esProduct.value?.sku,
          'brand': {
            '@type': 'Brand',
            'name': esProduct.value?.brand?.label,
          },
          'offers': {
            '@type': 'Offer',
            'url': `${config.baseDomain}${app.baseURL}${esProduct.value?.url}`.replace(/\/+/g, '/').replace(':/', '://'), // Safari compatible version
            'priceCurrency': currency,
            'price': esProduct.value?.finalPrice || 0,
            'itemCondition': 'https://schema.org/NewCondition',
            'availability': `https://schema.org/${m2Product.value?.stock_status === 'OUT_OF_STOCK' ? 'OutOfStock' : 'InStock'}`,
          },
          // ...{ review: reviews },
          ...{ aggregateRating: aggregateRating },
        },
        'tagPosition': 'bodyClose',
      },
    ],
  })
}

productSnippet()

// const { recentlyViewed } = useRecentlyViewed(esProduct?.value?.sku)
</script>

<style>
@import url("~layers/app/pages/~pdp.css");
</style>
